import { ISchedule, IScheduleEntry } from "@app-types/websocket/schedule.types";
import { ISuggestion, IScheduleChange } from '@app-types/websocket/suggestion.types';
import SCHEDULE_TYPES from "@constants/scheduleTypes";
import { SUGGESTION_CHANGE_TYPES } from '@constants/suggestionTypes';
import { isSameDay } from '@helpers/DateHelper';

export const applySuggestion:(schedule:ISchedule , suggestion:ISuggestion) => {schedule:ISchedule, success:boolean }
= (schedule:ISchedule , suggestion:ISuggestion) => {
    const scheduleCopy:ISchedule = JSON.parse(JSON.stringify(schedule));

    let nextId = 1000000;

    for (const change of suggestion.changes) {
        if (change.scheduleId !== scheduleCopy.id) {continue;}
        if (change.type === SUGGESTION_CHANGE_TYPES.DELETE_SCHEDULE) {
            scheduleCopy.data.scheduleEntriesParentA = {};
            scheduleCopy.data.scheduleEntriesParentB = {};
            scheduleCopy.status = SCHEDULE_TYPES.DELETED;
            return {schedule:scheduleCopy, success:true};
        }
        if (change.type === SUGGESTION_CHANGE_TYPES.CHANGE_CHILDREN && change.children) {
            scheduleCopy.data.children = [...change.children];
            continue;
        }
        const changeAppliedSuccessfully:boolean = applyChange(
            change, 
            change.isParentA?scheduleCopy.data.scheduleEntriesParentA:scheduleCopy.data.scheduleEntriesParentB,
            nextId);

        if (!changeAppliedSuccessfully) { 
            console.error("Failed to apply suggestion: "+suggestion.id+", change: "+change.type);
            return {schedule, success:false};
        }
        nextId++;
    }

    scheduleCopy.data.scheduleEntriesParentA = generateSide(sortMergeAndClean(scheduleCopy.data.scheduleEntriesParentA));
    scheduleCopy.data.scheduleEntriesParentB = generateSide(sortMergeAndClean(scheduleCopy.data.scheduleEntriesParentB));

    // TODO validate schedule ?

    return {schedule:scheduleCopy, success:true};
};

const applyChange : (change:IScheduleChange, scheduleEntries:{ [key:number]:IScheduleEntry; }, newId:number) => 
    boolean = (change:IScheduleChange, scheduleEntries:{ [key:number]:IScheduleEntry; }, newId:number) => {
        if (change.type === SUGGESTION_CHANGE_TYPES.ADD_SCHEDULE_ENTRY && change.newScheduleEntry !== undefined) {
            const newScheduleEntry:IScheduleEntry = {
              startAtSchool:change.newScheduleEntry.startAtSchool, 
              endAtSchool:change.newScheduleEntry.endAtSchool, 
              startTime:change.newScheduleEntry.startTime, 
              endTime: change.newScheduleEntry.endTime, 
              startLoc: change.newScheduleEntry.startLoc, 
              endLoc: change.newScheduleEntry.endLoc, 
              id:newId
            };
            scheduleEntries[""+newId] = newScheduleEntry;
            return true;
        } else if (change.type === SUGGESTION_CHANGE_TYPES.UPDATE_SCHEDULE_ENTRY 
        && change.newScheduleEntry !== undefined) {
            const existingEvent:IScheduleEntry|undefined = scheduleEntries[change.newScheduleEntry.id];

            if (!existingEvent)
                { return false; }
            if (change.oldScheduleEntry) {
                if (existingEvent.endAtSchool !== change.oldScheduleEntry.endAtSchool) {
                    return false;
                }
                if (existingEvent.endLoc !== change.oldScheduleEntry.endLoc) {
                    return false;
                }
                if (existingEvent.endTime !== change.oldScheduleEntry.endTime) {
                    return false;
                }
                if (existingEvent.startAtSchool !== change.oldScheduleEntry.startAtSchool) {
                    return false;
                }
                if (existingEvent.startLoc !== change.oldScheduleEntry.startLoc) {
                    return false;
                }
                if (existingEvent.startTime !== change.oldScheduleEntry.startTime) {
                    return false;
                }
            }
            scheduleEntries[change.newScheduleEntry.id] = change.newScheduleEntry;
            return true;
        } else if (change.type === SUGGESTION_CHANGE_TYPES.REMOVE_SCHEDULE_ENTRY && change.oldScheduleEntry !== undefined) {
            if (!scheduleEntries[change.oldScheduleEntry.id]) {
                return false;
            }
            delete scheduleEntries[change.oldScheduleEntry.id];
            return true;
        } else {
            return false;
        }
};

export const changeHandover = 
    (schedule:ISchedule, oldTime:number, newTime:number, atSchool:boolean, location?:string):ISchedule => {

    const aSideArray = adjustHandovers(schedule.data.scheduleEntriesParentA, oldTime, newTime, atSchool, location);
    const bSideArray = adjustHandovers(schedule.data.scheduleEntriesParentB, oldTime, newTime, atSchool, location);

    const aSide = sortAndMerge(aSideArray);
    const bSide = sortAndMerge(bSideArray);

    const newSchedule:ISchedule = {
        id:schedule.id,
        status:schedule.status,
        name:schedule.name,
        school:schedule.school,
        lastHandover:schedule.lastHandover,
        data: {
            parentA:schedule.data.parentA,
            parentB:schedule.data.parentB,
            children:schedule.data.children,
            scheduleEntriesParentA: aSide,
            scheduleEntriesParentB: bSide,
        }
    };

    return newSchedule;
};

const adjustHandovers = (scheduleSide:{[key: number]: IScheduleEntry;}, 
    oldTime:number, newTime:number, atSchool:boolean, location?:string):IScheduleEntry[] => {

        const entriesArray:IScheduleEntry[] = [];
        for ( const key of Object.keys(scheduleSide)) {
            const entry = scheduleSide[key];
            const entryCopy:IScheduleEntry = Object.assign({},entry);
            entryCopy.id = Number.parseInt(key,10);
            if (entryCopy.startTime === oldTime) {
                entryCopy.startTime = newTime;
                entryCopy.startLoc = location;
                entryCopy.startAtSchool = atSchool;
                entriesArray.push(entryCopy);
            } else if (entryCopy.endTime === oldTime) {
                entryCopy.endTime = newTime;
                entryCopy.endLoc = location;
                entryCopy.endAtSchool = atSchool;
                entriesArray.push(entryCopy);
            } else {
                entriesArray.push(entry);
            }
        }

        return entriesArray;
};

const sortAndMerge = (entries: IScheduleEntry[] ):{[key: string]: IScheduleEntry} => {

    entries.sort((a,b) => a.startTime-b.startTime);

    const finalEntries:IScheduleEntry[] = [];
    let prevEntry:IScheduleEntry|undefined;

    for(const entry of entries) {
        const entryCopy:IScheduleEntry = Object.assign({},entry);
        if (entryCopy.startTime >= entryCopy.endTime) {
            continue;
        }
        if (prevEntry !== undefined) {
            if (prevEntry.endTime >= entryCopy.startTime) {
                if (prevEntry.endTime < entryCopy.endTime) {
                    prevEntry.endTime = entryCopy.endTime;
                    prevEntry.endAtSchool = entryCopy.endAtSchool;
                    prevEntry.endLoc = entryCopy.endLoc;
                }
                continue;
            }
            finalEntries[prevEntry.id] = prevEntry;
        }
        prevEntry = entryCopy;
    }
    if (prevEntry) {
        finalEntries[prevEntry.id] = prevEntry;
    }

    const side : {[key: string]: IScheduleEntry} = {};
    for(const entry of entries) {
        side[entry.id] = entry;
    }
    return side;
};

export const addMidDayVisit 
    : (newID:number, 
        schedule:ISchedule,
        startTime:number, endTime:number, 
        startAtSchool:boolean, endAtSchool:boolean,
        startLocation?:string, endLocation?:string) => ISchedule 
    = (newID:number,
        schedule:ISchedule,
        startTime:number, endTime:number, 
        startAtSchool:boolean, endAtSchool:boolean,
        startLocation?:string, endLocation?:string) => {

    const newEntry:IScheduleEntry = {
        id: newID,
        endAtSchool,
        endTime,
        endLoc: endLocation,
        startAtSchool,
        startTime,
        startLoc: startLocation,
    };

    if (!isSameDay(new Date(startTime), new Date(endTime))) {
        return schedule;
    }

    const aSideEntries = sortMergeAndClean(schedule.data.scheduleEntriesParentA, startTime, endTime);
    const bSideEntries = sortMergeAndClean(schedule.data.scheduleEntriesParentB, startTime, endTime);

    const closestASideEntries = findBeforeAfterPerSide(aSideEntries, startTime, endTime);

    // A side is around this entry
    if (closestASideEntries.before !== undefined && closestASideEntries.after !== undefined
        && closestASideEntries.before.id === closestASideEntries.after.id
        && !isSameDay(new Date(closestASideEntries.before.endTime), new Date(startTime))
        && !isSameDay(new Date(closestASideEntries.after.startTime), new Date(endTime))) {
            const splitEntry:IScheduleEntry = {
                id: (newID+1),
                endAtSchool: closestASideEntries.after.endAtSchool,
                endTime: closestASideEntries.after.endTime,
                endLoc: closestASideEntries.after.endLoc,
                startAtSchool: endAtSchool,
                startTime: endTime,
                startLoc: endLocation,
            };
            
            closestASideEntries.before.endTime = startTime;
            closestASideEntries.before.endAtSchool = startAtSchool;
            closestASideEntries.before.endLoc = startLocation;

            aSideEntries.push(splitEntry);
            bSideEntries.push(newEntry);  
    // B side is around this entry
    } else {
        const closestBSideEntries = findBeforeAfterPerSide(bSideEntries, startTime, endTime);
        if (closestBSideEntries.before !== undefined && closestBSideEntries.after !== undefined
            && closestBSideEntries.before.id === closestBSideEntries.after.id
            && !isSameDay(new Date(closestBSideEntries.before.endTime), new Date(startTime))
            && !isSameDay(new Date(closestBSideEntries.after.startTime), new Date(endTime))) {

            const splitEntry:IScheduleEntry = {
                id: (newID+1),
                endAtSchool: closestBSideEntries.after.endAtSchool,
                endTime: closestBSideEntries.after.endTime,
                endLoc: closestBSideEntries.after.endLoc,
                startAtSchool: endAtSchool,
                startTime: endTime,
                startLoc: endLocation,
            };
            
            closestBSideEntries.before.endTime = startTime;
            closestBSideEntries.before.endAtSchool = startAtSchool;
            closestBSideEntries.before.endLoc = startLocation;

            bSideEntries.push(splitEntry);
            aSideEntries.push(newEntry);  
        // NO side is around this entry (adding a visit is impossible)
        } else {
            return schedule;
        }
    }

    const scheduleEntriesParentA:{ [key:number]:IScheduleEntry; } = generateSide(aSideEntries);
    const scheduleEntriesParentB:{ [key:number]:IScheduleEntry; } = generateSide(bSideEntries);

    const newSchedule:ISchedule = {
        id:schedule.id,
        status:schedule.status,
        name:schedule.name,
        school:schedule.school,
        lastHandover:schedule.lastHandover,
        data: {
            parentA:schedule.data.parentA,
            parentB:schedule.data.parentB,
            children:schedule.data.children,
            scheduleEntriesParentA,
            scheduleEntriesParentB,
        }
    };

    return newSchedule;

};

export const addDay 
    : (newID:number, schedule:ISchedule, forParentA: boolean,
        startTime:number, endTime:number, 
        startAtSchool:boolean, endAtSchool:boolean,
        startLocation?:string, endLocation?:string) => ISchedule 
    = (newID:number, schedule:ISchedule, forParentA: boolean,
        startTime:number, endTime:number, 
        startAtSchool:boolean, endAtSchool:boolean,
        startLocation?:string, endLocation?:string) => {

    const newEntry:IScheduleEntry = {
        id: newID,
        endAtSchool,
        endTime,
        endLoc: endLocation,
        startAtSchool,
        startTime,
        startLoc: startLocation,
    };
    
    const addSideEntries = sortMergeAndClean(
        forParentA?schedule.data.scheduleEntriesParentA:schedule.data.scheduleEntriesParentB,
        startTime, endTime);
    const otherSideEntries = sortMergeAndClean(
        forParentA?schedule.data.scheduleEntriesParentB:schedule.data.scheduleEntriesParentA,
        startTime, endTime);

    const closestAddSideEntries = findBeforeAfterPerSide(addSideEntries, startTime, endTime);
    const closestOtherSideEntries = findBeforeAfterPerSide(otherSideEntries, startTime, endTime);

    const closestEntries = findBeforeAfter(closestAddSideEntries,closestOtherSideEntries);

    // console.log(JSON.stringify(closestEntries));


    if (closestEntries.beforeEntry !== undefined && closestEntries.afterEntry !== undefined) {
        
        if (closestEntries.beforeIsAddSide && closestEntries.afterIsAddSide) { 
            // this added entry is inside an already existing entry. we dont need to do anything
            if (closestEntries.beforeEntry.id === closestEntries.afterEntry.id) {

            // We need to merge existing two entries
            } else {
                const i = addSideEntries.indexOf(closestEntries.afterEntry);
                addSideEntries.splice(i,1);
                closestEntries.beforeEntry.endTime = closestEntries.afterEntry.endTime;
                closestEntries.beforeEntry.endAtSchool = closestEntries.afterEntry.endAtSchool;
                closestEntries.beforeEntry.endLoc = closestEntries.afterEntry.endLoc;
            }

        // this added entry is inside an already existing otherside entry.
        } else if (!closestEntries.beforeIsAddSide && !closestEntries.afterIsAddSide) { 
            // this added entry is inside an already existing entry. possibly Split entry
            if (closestEntries.beforeEntry.id === closestEntries.afterEntry.id) {

                // we should just move times
                if (isSameDay(new Date(endTime), new Date(closestEntries.afterEntry.endTime))) {
                    // console.log("move times 1");
                    if (closestAddSideEntries.after) {
                        closestAddSideEntries.after.startTime = startTime;
                        closestAddSideEntries.after.startAtSchool = startAtSchool;
                        closestAddSideEntries.after.startLoc = startLocation;
                    } else {
                        addSideEntries.push(newEntry); 
                    }
                    closestEntries.beforeEntry.endTime = startTime;
                    closestEntries.beforeEntry.endAtSchool = startAtSchool;
                    closestEntries.beforeEntry.endLoc = startLocation;
                // we should just move times
                } else if (isSameDay(new Date(startTime), new Date(closestEntries.beforeEntry.startTime))) {
                    // console.log("move times 2");
                    if (closestAddSideEntries.before) {
                        closestAddSideEntries.before.endTime = endTime;
                        closestAddSideEntries.before.endAtSchool = endAtSchool;
                        closestAddSideEntries.before.endLoc = endLocation;
                    } else {
                        addSideEntries.push(newEntry);    
                    }
                    closestEntries.afterEntry.startTime = endTime;
                    closestEntries.afterEntry.startAtSchool = endAtSchool;
                    closestEntries.afterEntry.startLoc = endLocation;
                // it is a split!
                } else {
                    // console.log("split");
                    const splitEntry:IScheduleEntry = {
                        id: (newID+1),
                        endAtSchool: closestEntries.afterEntry.endAtSchool,
                        endTime: closestEntries.afterEntry.endTime,
                        endLoc: closestEntries.afterEntry.endLoc,
                        startAtSchool: endAtSchool,
                        startTime: endTime,
                        startLoc: endLocation,
                    };
                    
                    closestEntries.beforeEntry.endTime = startTime;
                    closestEntries.beforeEntry.endAtSchool = startAtSchool;
                    closestEntries.beforeEntry.endLoc = startLocation;
    
                    otherSideEntries.push(splitEntry);
                    addSideEntries.push(newEntry);    
                }
            // this should not be possible but lets fix it
            } else {
                closestEntries.beforeEntry.endTime = startTime;
                closestEntries.beforeEntry.endAtSchool = startAtSchool;
                closestEntries.beforeEntry.endLoc = startLocation;
    
                closestEntries.afterEntry.startTime = endTime;
                closestEntries.afterEntry.startAtSchool = endAtSchool;
                closestEntries.afterEntry.startLoc = endLocation;

                addSideEntries.push(newEntry);
            }
        // entry before is same side, entry after other side
        } else if (closestEntries.beforeIsAddSide) { 
            
            closestEntries.beforeEntry.endTime = endTime;
            closestEntries.beforeEntry.endAtSchool = endAtSchool;
            closestEntries.beforeEntry.endLoc = endLocation;

            closestEntries.afterEntry.startTime = endTime;
            closestEntries.afterEntry.startAtSchool = endAtSchool;
            closestEntries.afterEntry.startLoc = endLocation;

        // entry before is other side, entry after same side
        } else if (closestEntries.afterIsAddSide) { 
            closestEntries.beforeEntry.endTime = startTime;
            closestEntries.beforeEntry.endAtSchool = startAtSchool;
            closestEntries.beforeEntry.endLoc = startLocation;

            closestEntries.afterEntry.startTime = startTime;
            closestEntries.afterEntry.startAtSchool = startAtSchool;
            closestEntries.afterEntry.startLoc = startLocation;
        } else {
            console.error("invalid schedule calculations!");
        }
    } else if (closestEntries.beforeEntry) { // this added entry is added to the end of a schedule
        
        if (closestEntries.beforeIsAddSide) { // just extend existing entry
            closestEntries.beforeEntry.endTime = endTime;
            closestEntries.beforeEntry.endAtSchool = endAtSchool;
            closestEntries.beforeEntry.endLoc = endLocation;
        
        } else { // add new entry and connect them
            closestEntries.beforeEntry.endTime = startTime;
            closestEntries.beforeEntry.endAtSchool = startAtSchool;
            closestEntries.beforeEntry.endLoc = startLocation;

            addSideEntries.push(newEntry);
        }

    } else if (closestEntries.afterEntry) { // this added entry is added to the beginning of a schedule
        
        if (closestEntries.afterIsAddSide) { // just extend existing entry
            closestEntries.afterEntry.startTime = startTime;
            closestEntries.afterEntry.startAtSchool = startAtSchool;
            closestEntries.afterEntry.startLoc = startLocation;
        
        } else { // add new entry and connect them
            closestEntries.afterEntry.startTime = endTime;
            closestEntries.afterEntry.startAtSchool = endAtSchool;
            closestEntries.afterEntry.startLoc = endLocation;

            addSideEntries.push(newEntry);
        }
    } else { // this added entry is the only entry
        addSideEntries.push(newEntry);
    }

    const scheduleEntriesParentA:{ [key:number]:IScheduleEntry; } = generateSide(forParentA?addSideEntries:otherSideEntries);
    const scheduleEntriesParentB:{ [key:number]:IScheduleEntry; } = generateSide(forParentA?otherSideEntries:addSideEntries);

    const newSchedule:ISchedule = {
        id:schedule.id,
        status:schedule.status,
        name:schedule.name,
        school:schedule.school,
        lastHandover:schedule.lastHandover,
        data: {
            parentA:schedule.data.parentA,
            parentB:schedule.data.parentB,
            children:schedule.data.children,
            scheduleEntriesParentA,
            scheduleEntriesParentB,
        }
    };
    // console.log(JSON.stringify(newSchedule));
    return newSchedule;

};

// sorts all entries on time order
// merges overlapping entries
// removes entries inside time interval (if defined)
const sortMergeAndClean = (scheduleEntries:{ [key:number]:IScheduleEntry; }, startTime?:number, endTime?:number) => {
    const entries =(Object.values(scheduleEntries)).sort((a,b) => a.startTime-b.startTime);
    const finalEntries:IScheduleEntry[] = [];
    let prevEntry:IScheduleEntry|undefined;

    for(const entry of entries) {
        const entryCopy:IScheduleEntry = Object.assign({},entry);
        if (entryCopy.startTime >= entryCopy.endTime) {
            continue;
        }
        if (prevEntry !== undefined) {
            if (prevEntry.endTime >= entryCopy.startTime) {
                console.log("entry "+prevEntry.id+" and "+entryCopy.id+" should be merged"); // TODO implement
                if (prevEntry.endTime < entryCopy.endTime) {
                    prevEntry.endTime = entryCopy.endTime;
                    prevEntry.endAtSchool = entryCopy.endAtSchool;
                    prevEntry.endLoc = entryCopy.endLoc;
                    
                }
                continue;
            }
        }
        prevEntry = entryCopy;

        if ((startTime !== undefined && endTime !== undefined) &&
            ((entryCopy.startTime >= startTime || isSameDay(new Date(entryCopy.startTime), new Date(startTime)) )
            && (entryCopy.endTime <= endTime || isSameDay(new Date(entryCopy.endTime), new Date(endTime))))) {
            continue;
        } else {
            finalEntries.push(entryCopy);
        }
    }
    return finalEntries;
};


const findBeforeAfter = (addSide:IBeforeAfter, otherSide:IBeforeAfter) => {
    let beforeIsAddSide:boolean|undefined;
    let afterIsAddSide:boolean|undefined;
    let beforeEntry:IScheduleEntry|undefined;
    let afterEntry:IScheduleEntry|undefined;
    
    if (addSide.before) {
        if (otherSide.before && otherSide.before.endTime > addSide.before.endTime) {
            beforeIsAddSide = false;
            beforeEntry = otherSide.before;
        } else {
            beforeIsAddSide = true;
            beforeEntry = addSide.before;
        }
    } else if (otherSide.before) {
        beforeIsAddSide = false;
        beforeEntry = otherSide.before;
    }

    if (addSide.after) {
        if (otherSide.after && otherSide.after.startTime < addSide.after.startTime) {
            afterIsAddSide = false;
            afterEntry = otherSide.after;
        } else {
            afterIsAddSide = true;
            afterEntry = addSide.after;
        }
    } else if (otherSide.after) {
        afterIsAddSide = false;
        afterEntry = otherSide.after;
    }

    return {beforeIsAddSide, afterIsAddSide, beforeEntry, afterEntry};
};

const findBeforeAfterPerSide = (scheduleEntries:IScheduleEntry[], startTime:number, endTime:number):IBeforeAfter => {

    let entryBefore:IScheduleEntry|undefined; // entry starting before startTime with the highest own startTime
    let entryAfter:IScheduleEntry|undefined; // entries ending after endTime with the lowest own startTime

    for(const entry of scheduleEntries) {
        if (entry.startTime < startTime ) {
            if (entryBefore === undefined) {
                entryBefore = entry;
            } else {
                if (entry.startTime > entryBefore.startTime) {
                    entryBefore = entry;
                }
            }
        }

        if (entry.endTime > endTime) {
            if (entryAfter === undefined) {
                entryAfter = entry;
            } else {
                if (entry.startTime < entryAfter.startTime) {
                    entryAfter = entry;
                }
            }
        }
    }

    return {before:entryBefore, after: entryAfter, };
};

const generateSide 
    : (scheduleEntries:IScheduleEntry[]) => { [key:number]:IScheduleEntry; } 
    = (scheduleEntries:IScheduleEntry[]) => {
    const side:{ [key:number]:IScheduleEntry; } = {};
    for (const entry of scheduleEntries) {
        side[entry.id] = entry;
    }
    return side;
};

export interface IScheduleResult {
    schedule: number;
    alteredDays: string[];
}
export interface IBeforeAfter {
    before: IScheduleEntry|undefined;
    after: IScheduleEntry|undefined;
}


// WEBPACK FOOTER //
// ./src/helpers/calendar/scheduleCalculator.ts